import React, { useState, useEffect } from 'react';
import { 
  Building2, 
  BookOpen, 
  Clock, 
  HelpCircle, 
  Play, 
  FileText, 
  Lock, 
  Sparkles, 
  ArrowLeft, 
  Inbox, 
  CheckCircle2, 
  MapPin,
  ShieldAlert,
  ChevronRight,
  PenTool,
  AlertCircle,
  Layers
} from 'lucide-react';
import { ExamCatalogEntry } from '../data/examCatalog';
import { PraxisPaper, UserProfile } from '../types';
import { getPaperCatalogSummaries, isPaperUnlockedForUser } from '../lib/firebase';
import { PRAXIS_PAPERS } from '../data/praxisPapers';
import { STATE_STANDARDS } from '../data/stateStandards';
import { ParaPathwayScoreTable } from './ParaPathwayScoreTable';

interface ExamDetailPageProps {
  entry: ExamCatalogEntry;
  userProfile: UserProfile;
  onStartTest: (paperId: string) => void;
  onNavigateToMockExams?: (familyOrState: string) => void;
  onOpenGuides: (paperId: string) => void;
  onOpenResources?: (paperId: string) => void;
  // Guests and free accounts: open the free preview for this paper.
  onTryFree?: (paperId: string) => void;
  openPricingModal: () => void;
  onBackToCatalog: () => void;
}

export const ExamDetailPage: React.FC<ExamDetailPageProps> = ({
  entry,
  userProfile,
  onStartTest,
  onNavigateToMockExams,
  onOpenGuides,
  onOpenResources,
  onTryFree,
  openPricingModal,
  onBackToCatalog
}) => {
  const [papers, setPapers] = useState<PraxisPaper[]>(PRAXIS_PAPERS);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    async function loadPapers() {
      try {
        const firestorePapers = await getPaperCatalogSummaries();
        if (firestorePapers && firestorePapers.length > 0) {
          setPapers(firestorePapers);
          // Debug log (console only): prints distinct examCode & code values from Firestore
          const distinctExamCodes = Array.from(new Set(firestorePapers.map(p => p.examCode || '(missing examCode)')));
          const summary = firestorePapers.map(p => ({ id: p.id, code: p.code, examCode: p.examCode, title: p.title }));
          console.log(`[ExamDirectory Debug] Loaded ${firestorePapers.length} papers for "${entry.state}" (${entry.id}). Distinct examCode values:`, distinctExamCodes);
          console.log(`[ExamDirectory Debug] Paper details summary:`, summary);
        }
      } catch (err) {
        console.warn('ExamDetailPage: could not load papers from Firestore, using static list:', err);
      } finally {
        setLoading(false);
      }
    }
    loadPapers();
  }, [entry.id, entry.state]);

  const activeStateStandard = STATE_STANDARDS.find(
    s => s.name.toLowerCase() === entry.state.toLowerCase() || s.code.toLowerCase() === entry.id.toLowerCase()
  ) || STATE_STANDARDS[0];

  return (
    <div className="space-y-8 max-w-7xl mx-auto py-2">
      {/* Top Header Banner */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-100">
          <div className="space-y-1">
            <button
              onClick={onBackToCatalog}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition mb-1"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to State Exam Directory
            </button>
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-100 text-indigo-800 border border-indigo-200">
                {entry.isPathway ? 'National Certification Pathway' : 'State Certification'}
              </span>
              <span className="text-xs font-medium text-slate-500">
                {entry.examCodes.length} Sub-Exam Categories
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              {entry.isPathway ? entry.state : `${entry.state} Certification Exams`}
            </h1>
            <p className="text-sm text-slate-600 font-medium">
              Primary Exam Requirements: <span className="font-bold text-slate-800">{entry.examLabel}</span>
            </p>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-xs space-y-1 shrink-0 max-w-xs">
            <div className="flex items-center gap-2 font-bold text-slate-800">
              <MapPin className="w-4 h-4 text-indigo-600" /> {entry.isPathway ? 'Qualifying Scores' : 'State Standards'}
            </div>
            <p className="text-slate-500 text-[11px] leading-relaxed">
              {entry.isPathway
                ? 'Not tied to any single state -- each state sets its own qualifying score. See the full breakdown below.'
                : `Passing benchmark scores and domain weightages configured for ${entry.state} candidate requirements.`}
            </p>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-indigo-50/80 border border-indigo-100 rounded-xl p-4 flex items-start gap-3 text-xs text-indigo-950">
          <Sparkles className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />
          <div className="space-y-0.5">
            <span className="font-bold">Exam Category Sub-Sections</span>
            <p className="text-indigo-800/90 leading-relaxed text-[11px]">
              Select any sub-exam code below to access full timed practice tests, diagnostic questions, and study guides.
            </p>
          </div>
        </div>
      </div>

      {entry.isPathway && <ParaPathwayScoreTable />}

      {/* Sub-Exam Code Sections */}
      <div className="space-y-8">
        {entry.examCodes.map((examCode) => {
          // Primary match: exact stored examCode value from Firestore reference list
          const matchingPapers = papers.filter((p) => {
            const paperExamCode = p.examCode ? p.examCode.trim() : '';
            const targetExamCode = examCode.trim();

            // 1. Exact case-insensitive match on examCode field
            if (paperExamCode && paperExamCode.toLowerCase() === targetExamCode.toLowerCase()) {
              return true;
            }

            // 2. Family prefix match on examCode (e.g. "CSET-MultipleSubjects" matches family "CSET", "NYSTCE-EAS" matches "NYSTCE")
            if (paperExamCode) {
              const lowerP = paperExamCode.toLowerCase();
              const lowerTarget = targetExamCode.toLowerCase();
              if (lowerP.startsWith(lowerTarget + '-') || lowerP === lowerTarget) {
                return true;
              }
            }

            // 3. Subtest code lookup (treat document id as opaque; check code field)
            if (p.code) {
              const lowerCode = p.code.trim().toLowerCase();
              const lowerTarget = targetExamCode.toLowerCase();
              if (lowerCode.startsWith(lowerTarget + '-') || lowerCode === lowerTarget) {
                return true;
              }
            }

            // 4. Graceful fallback heuristics for legacy documents missing examCode
            if (targetExamCode === 'Praxis-Core' && p.category === 'core' && (!paperExamCode || paperExamCode.startsWith('Praxis'))) return true;
            if (targetExamCode === 'Praxis-Elementary' && (p.category === 'elementary' || p.code === '5001')) return true;
            if (targetExamCode === 'Praxis-Subject' && (p.category === 'subject' || p.category === 'elementary')) return true;
            if (targetExamCode === 'Praxis-PLT' && p.category === 'plt') return true;

            return false;
          });

          // Temporary debug log (console only): surfaces distinct examCode values when zero results are found
          if (matchingPapers.length === 0) {
            const distinctExamCodes = Array.from(new Set(papers.map(p => p.examCode || '(missing examCode)')));
            console.warn(
              `[ExamDirectory Debug] Zero results for exam family "${examCode}" on directory entry "${entry.id}" (${entry.state}). ` +
              `Total papers in store: ${papers.length}. ` +
              `Distinct examCode values in store:`,
              distinctExamCodes,
              `Available papers:`,
              papers.map(p => ({ id: p.id, code: p.code, examCode: p.examCode, title: p.title }))
            );
          }

          return (
            <div key={examCode} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-5">
              {/* Section Header */}
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 text-indigo-700 font-extrabold text-sm flex items-center justify-center">
                    {(examCode || 'EX').slice(0, 3)}
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                      <span>{examCode} Exam</span>
                      <span className="text-xs font-semibold px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full">
                        {matchingPapers.length} Paper{matchingPapers.length === 1 ? '' : 's'} Available
                      </span>
                    </h2>
                    <p className="text-xs text-slate-500">
                      {entry.isPathway
                        ? 'Selected-response practice questions for paraprofessional/paraeducator certification'
                        : `State certification requirement for ${entry.state} candidates`}
                    </p>
                  </div>
                </div>
              </div>

              {/* Papers Grid or Empty State */}
              {matchingPapers.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {matchingPapers.map((paper) => {
                    // Bundle papers (5001) have no questions of their own:
                    // show what the exam is and link to each subtest.
                    if (Array.isArray(paper.bundleOf) && paper.bundleOf.length > 0) {
                      const subtests = paper.bundleOf
                        .map((sid) => papers.find((p) => p.id === sid || p.code === sid))
                        .filter((p): p is PraxisPaper => !!p);
                      return (
                        <div
                          key={paper.id}
                          className="rounded-xl border border-[#0f1b4d]/20 bg-white hover:shadow-md transition-all flex flex-col justify-between overflow-hidden md:col-span-2 lg:col-span-3"
                        >
                          <div className="p-5 space-y-3">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="px-2.5 py-1 rounded text-xs font-bold bg-[#0f1b4d] text-white">PRAXIS {paper.code}</span>
                              <span className="px-2 py-0.5 bg-teal-50 text-teal-800 font-bold text-[10px] rounded border border-teal-200">
                                Taken as {paper.bundleOf.length} subtests
                              </span>
                              {paper.durationMinutes > 0 && (
                                <span className="text-xs text-slate-500 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {paper.durationMinutes} mins in total</span>
                              )}
                            </div>
                            <div>
                              <h3 className="font-bold text-slate-900 text-sm">{paper.title}</h3>
                              <p className="text-xs text-slate-500 mt-1 leading-relaxed">{paper.description}</p>
                            </div>
                            <p className="text-xs text-slate-600">
                              Each subtest is timed and scored on its own, and you can take them on different days. Practice them one at a time below.
                            </p>
                          </div>
                          <div className="p-4 bg-slate-50 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                            {subtests.map((sub) => {
                              const subUnlocked = isPaperUnlockedForUser(userProfile, sub.code) || isPaperUnlockedForUser(userProfile, sub.id);
                              const open = () => {
                                if (subUnlocked) onStartTest(sub.id);
                                else if (onTryFree) onTryFree(sub.id);
                                else openPricingModal();
                              };
                              return (
                                <button
                                  key={sub.id}
                                  type="button"
                                  onClick={open}
                                  className="text-left bg-white border border-slate-200 hover:border-[#0E9594] rounded-lg p-3 transition cursor-pointer"
                                >
                                  <div className="text-[11px] font-extrabold text-[#0E9594]">PRAXIS {sub.code}</div>
                                  <div className="text-xs font-bold text-slate-800 leading-snug line-clamp-2">{sub.title.replace(/^Elementary Education:\s*/i, '')}</div>
                                  <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
                                    <Play className="w-3 h-3" /> {subUnlocked ? 'Start practice' : 'Try 5 free questions'}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }
                    const targetScore = activeStateStandard.passingScores[paper.code] ?? (paper.passingScoreEts !== null && paper.passingScoreEts !== undefined ? paper.passingScoreEts : null);
                    const isUnlocked = isPaperUnlockedForUser(userProfile, paper.code) || isPaperUnlockedForUser(userProfile, paper.id);
                    const isEssayPaper = paper.contentType === 'essay' || (Array.isArray(paper.essayPrompts) && paper.essayPrompts.length > 0);
                    const isMixedPaper = paper.contentType === 'mixed';
                    const isPerformanceAssessment = paper.contentType === 'performance_assessment';
                    const promptCount = paper.essayPrompts?.length || 0;

                    // Compute dynamic MC & CR question counts across domains
                    const totalMcCount = paper.domains && paper.domains.length > 0 && paper.domains.some(d => d.mcCount !== undefined)
                      ? paper.domains.reduce((sum, d) => sum + (d.mcCount || 0), 0)
                      : (paper.questions?.length || paper.totalQuestions || 0);

                    const totalCrCount = paper.domains && paper.domains.length > 0 && paper.domains.some(d => d.crCount !== undefined)
                      ? paper.domains.reduce((sum, d) => sum + (d.crCount || 0), 0)
                      : (paper.constructedResponsePrompts?.length || 0);

                    return (
                      <div
                        key={paper.id}
                        className="rounded-xl border border-slate-200 bg-slate-50/50 hover:bg-white hover:shadow-md transition-all flex flex-col justify-between overflow-hidden"
                      >
                        <div className="p-5 space-y-3">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="px-2.5 py-1 rounded text-xs font-bold bg-indigo-100 text-indigo-800 border border-indigo-200">
                                {paper.examCode && !paper.examCode.toLowerCase().startsWith('praxis')
                                  ? (paper.code.toLowerCase().startsWith('nystce-') || paper.code.toLowerCase().startsWith('cset-') || paper.code.toLowerCase().startsWith('cbest-')
                                      ? paper.code
                                      : `${paper.examCode} ${paper.code}`)
                                  : `PRAXIS ${paper.code}`}
                              </span>
                              {isEssayPaper && (
                                <span className="px-2 py-0.5 bg-amber-100 text-amber-900 font-bold text-[10px] rounded border border-amber-200">
                                  Essay
                                </span>
                              )}
                              {isMixedPaper && (
                                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-900 font-bold text-[10px] rounded border border-emerald-200">
                                  MC + CR
                                </span>
                              )}
                              {isPerformanceAssessment && (
                                <span className="px-2 py-0.5 bg-sky-100 text-sky-900 font-bold text-[10px] rounded border border-sky-200">
                                  Portfolio
                                </span>
                              )}
                            </div>
                            {paper.isPassingScoreProvisional || paper.passingScoreNote ? (
                              <span className="text-xs font-semibold text-amber-800 bg-amber-50 px-2 py-0.5 rounded border border-amber-200" title={paper.passingScoreNote}>
                                Passing Score: {targetScore || 240} (provisional)
                              </span>
                            ) : targetScore !== null ? (
                              <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                                Passing Score: {targetScore}
                              </span>
                            ) : (
                              <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded border border-slate-200 italic">
                                Passing score: Not yet confirmed
                              </span>
                            )}
                          </div>

                          <div>
                            <h3 className="font-bold text-slate-900 text-sm line-clamp-1">
                              {paper.title}
                            </h3>
                            <p className="text-xs text-slate-500 line-clamp-2 mt-1">
                              {paper.description}
                            </p>
                          </div>

                          {paper.statusNote && (
                            <div className="p-2.5 bg-sky-50 border border-sky-200 rounded-lg text-xs text-sky-950 leading-relaxed flex items-start gap-1.5">
                              <AlertCircle className="w-3.5 h-3.5 text-sky-600 mt-0.5 shrink-0" />
                              <span>{paper.statusNote}</span>
                            </div>
                          )}

                          {(paper.isPassingScoreProvisional || paper.passingScoreNote) && targetScore !== null && (
                            <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-950 leading-relaxed flex items-start gap-1.5">
                              <AlertCircle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
                              <span>
                                Passing score: <strong>{targetScore}</strong> ({paper.passingScoreNote || 'provisional — based on other states using this exam; not yet confirmed by the California CTC'})
                              </span>
                            </div>
                          )}

                          {isPerformanceAssessment ? (
                            <div className="flex items-center justify-between text-xs text-sky-950 bg-sky-50/80 p-2.5 rounded-lg border border-sky-200">
                              <div className="flex items-center gap-1.5 font-semibold">
                                <Layers className="w-3.5 h-3.5 text-sky-700" />
                                <span>Portfolio-Based Assessment</span>
                              </div>
                              <span className="text-[11px] font-bold text-sky-800 bg-white px-2 py-0.5 rounded border border-sky-200">Clinical Practicum</span>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between text-xs text-slate-600 bg-white p-2.5 rounded-lg border border-slate-100">
                              <div className="flex items-center gap-1.5">
                                <Clock className="w-3.5 h-3.5 text-slate-400" />
                                <span>{paper.durationMinutes && paper.durationMinutes > 0 ? `${paper.durationMinutes} mins` : 'Duration not yet confirmed'}</span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                {isEssayPaper ? (
                                  <>
                                    <PenTool className="w-3.5 h-3.5 text-amber-600" />
                                    <span className="font-semibold text-amber-900">{promptCount} {promptCount === 1 ? 'Essay Prompt' : 'Essay Prompts'}</span>
                                  </>
                                ) : isMixedPaper ? (
                                  <>
                                    <HelpCircle className="w-3.5 h-3.5 text-indigo-600" />
                                    <span className="font-semibold text-slate-800">
                                      {totalMcCount} MC Questions · {totalCrCount} Constructed Responses
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <HelpCircle className="w-3.5 h-3.5 text-slate-400" />
                                    <span>{totalMcCount || paper.totalQuestions || 0} Qs</span>
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="p-4 bg-white border-t border-slate-100 flex items-center flex-wrap gap-2">
                          {isUnlocked ? (
                            <>
                              <button
                                onClick={() => onStartTest(paper.id)}
                                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs py-2 px-3 rounded-lg transition shadow-xs flex items-center justify-center gap-1.5 cursor-pointer"
                              >
                                <Play className="w-3.5 h-3.5" /> Start Practice & View Blueprints
                              </button>
                              <button
                                onClick={() => onOpenGuides(paper.id)}
                                className="bg-slate-100 border border-slate-200 hover:bg-slate-200 text-slate-700 font-semibold text-xs px-3 py-2 rounded-lg transition flex items-center gap-1 cursor-pointer"
                              >
                                <FileText className="w-3.5 h-3.5 text-slate-500" /> Guide
                              </button>
                              {onOpenResources && (
                                <button
                                  onClick={() => onOpenResources(paper.id)}
                                  className="bg-slate-100 border border-slate-200 hover:bg-slate-200 text-slate-700 font-semibold text-xs px-3 py-2 rounded-lg transition flex items-center gap-1 cursor-pointer"
                                >
                                  <Layers className="w-3.5 h-3.5 text-slate-500" /> Flashcards
                                </button>
                              )}
                            </>
                          ) : (
                            <>
                            {onTryFree && (
                              <button
                                onClick={() => onTryFree(paper.id)}
                                className="w-full bg-[#0E9594] hover:bg-[#0b7f7e] text-white font-bold text-xs py-2 px-3 rounded-lg transition shadow-xs flex items-center justify-center gap-1.5 cursor-pointer"
                              >
                                <Play className="w-3.5 h-3.5" /> Try 5 free questions
                              </button>
                            )}
                            <button
                              onClick={openPricingModal}
                              className="w-full bg-slate-800 hover:bg-slate-900 text-white font-bold text-xs py-2 px-3 rounded-lg transition shadow-xs flex items-center justify-center gap-2 cursor-pointer"
                            >
                              <Lock className="w-3.5 h-3.5 text-amber-300" /> Unlock with Subscription
                            </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Direct Link to Dedicated Mock Exams Hub (Pre-filtered to this Exam Family) */}
                {onNavigateToMockExams && (
                  <div className="p-4 bg-indigo-50/70 border border-indigo-200/80 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl bg-indigo-600 text-white flex items-center justify-center shrink-0 mt-0.5">
                        <Sparkles className="w-4 h-4" />
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-indigo-950">
                          Looking for Full Standardized Mock Exams for {entry.shortLabel}?
                        </h4>
                        <p className="text-[11px] text-indigo-800/90 leading-relaxed">
                          Access locked, timed full-length simulations with official domain compositions and unofficial score reports in the Mock Exams hub.
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => onNavigateToMockExams(entry.id || entry.shortLabel)}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-extrabold rounded-xl transition shadow-xs flex items-center gap-1.5 shrink-0 cursor-pointer"
                    >
                      <span>View {entry.shortLabel} Mock Exams</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </>
              ) : (
                /* Empty state as requested */
                <div className="p-8 text-center bg-slate-50/80 rounded-xl border border-dashed border-slate-300 space-y-3">
                  <div className="w-10 h-10 rounded-full bg-slate-200/80 text-slate-400 flex items-center justify-center mx-auto">
                    <Inbox className="w-5 h-5" />
                  </div>
                  <div className="space-y-1">
                    <h4 className="text-sm font-bold text-slate-700">
                      {examCode} Papers Coming Soon
                    </h4>
                    <p className="text-xs text-slate-500 max-w-md mx-auto leading-relaxed">
                      Papers for this exam haven't been added yet. Check back soon.
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
